/**
 * <hy-predictive-search>: Verbesserung für das Sucheingabefeld aus
 * snippets/hy-predictive-search-input.liquid (Suchseite AP-77, Header —
 * dieselbe Komponente, mehrere Aufrufstellen). Reine Erweiterung — ohne
 * dieses Modul bleibt das Feld ein gewöhnliches <input type="search"
 * name="q">, das sein Formular klassisch an routes.search_url absendet.
 *
 * ARIA-Combobox-Attribute (role="combobox", aria-expanded,
 * aria-controls, aria-activedescendant, aria-autocomplete) setzt
 * ausschließlich dieses Modul, nicht das Liquid-Snippet — Ready-Flag-
 * Idiom auf ARIA angewandt (M5-Plan §5.5): ohne JavaScript kündigt das
 * Feld keine Combobox an, die sich nie öffnet. Die Listbox trägt ihre
 * Rolle dagegen bereits statisch — eine leere, nicht fokussierte Liste ist
 * harmlos, anders als ein Feld, das fälschlich ein Popup verspricht.
 *
 * Zwei getrennte AbortController: #controller für den Lebenszyklus
 * (Projektmuster), #requestController pro Anfrage — ein Abbruch des
 * einen darf den anderen nicht mitreißen.
 *
 * Search panel (header_style "modern", AP-268 stage 2): optional, detected
 * by [data-hy-search-panel] inside the element. Only then the element gets
 * data-hy-search-state (idle | suggest | loading | results | empty) and the
 * CSS in assets/hy-header-modern.css shows the matching server-rendered
 * block. Results come from the section named in the input's
 * data-section-id and may be grouped ([data-hy-search-part]). Without the
 * panel (classic header, search page, drawer search) no state attribute
 * is set and the behaviour stays as before. The former header toggle path
 * ([data-hy-predictive-search-toggle]) had no markup left and is removed.
 *
 * Fehlerbehandlung (M5-Plan §5.4): nur 429 ist vorübergehend (Retry-After
 * gelesen, Fallback 5s, kein Auto-Retry). 404/417/422 sind für die
 * Seitenlebensdauer terminal (#disabled = true) — ein Retry pro
 * Tastenanschlag auf einen dauerhaften Fehler wäre der Anfragen-Sturm,
 * den R11 gerade vermeiden soll. In jedem Fehlerfall bleibt Enter ein
 * funktionierender, klassischer Sprung nach routes.search_url, weil
 * dieses Modul dafür nie preventDefault() aufruft.
 */
class HyPredictiveSearch extends HTMLElement {
  #controller = null;
  #requestController = null;
  #debounceTimer = null;
  #disabled = false;
  #blockedUntil = 0;
  #lastQuery = '';
  #activeIndex = -1;
  #input = null;
  #listbox = null;
  #dropdown = null;
  #suggest = null;
  #status = null;
  #dismissed = false;
  #dialog = null;
  #opener = null;
  #desktopQuery = null;
  #overlayActive = false;
  #clear = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Drawer search in header "modern" (AP-268 stage 4) only opens the mobile
    // search dialog (assets/hy-header-nav.js); it stays a plain form field.
    if (this.hasAttribute('data-hy-search-overlay-trigger')) return;

    this.#input = this.querySelector('[data-hy-predictive-search-input]');
    this.#listbox = this.#input ? document.getElementById(this.#input.dataset.listboxId) : null;
    if (!this.#input || !this.#listbox) return;

    this.#input.setAttribute('role', 'combobox');
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.setAttribute('aria-controls', this.#listbox.id);
    this.#input.setAttribute('aria-autocomplete', 'list');
    this.#input.setAttribute('autocomplete', 'off');

    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });

    // Clear button (header "modern", AP-268 stage 4): hidden while the field
    // is empty; a click empties it and runs the normal input path.
    this.#clear = this.querySelector('[data-hy-search-clear]');
    if (this.#clear) {
      this.#clear.addEventListener('click', () => {
        this.#input.value = '';
        this.#input.focus();
        this.#input.dispatchEvent(new Event('input', { bubbles: true }));
      }, { signal });
      this.#syncClear();
    }

    this.#dropdown = this.querySelector('[data-hy-search-panel]');
    if (this.#dropdown) {
      this.#suggest = this.#dropdown.querySelector('[data-hy-search-suggest]');
      this.#status = this.querySelector('[data-hy-search-status]');
      this.addEventListener('focusin', this.#handleFocusIn, { signal });
      // Safari does not focus links on click: without this, focusout would
      // close the panel before the click lands on a suggestion or result.
      this.#dropdown.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      this.#setState('idle');
    }

    this.#dialog = this.closest('.hy-header__search-dialog');
    if (this.#dialog) this.#setupOverlay(signal);
  }

  disconnectedCallback() {
    // Removed while the search dialog is open (theme editor re-render): the
    // 'close' event would only fire after abort() removed its listener, so the
    // page scroll lock is released here directly.
    if (this.#overlayActive) {
      this.#overlayActive = false;
      document.documentElement.style.overflow = '';
      if (this.#dialog?.open) this.#dialog.close();
    }
    this.#controller?.abort();
    this.#requestController?.abort();
    clearTimeout(this.#debounceTimer);
  }

  // Mobile search dialog (header_style "modern", AP-268 stage 4): the header
  // instance sits in <dialog class="hy-header__search-dialog" open role="none">.
  // From 48rem the dialog is display: contents and never opened; below it the
  // search button ([data-hy-search-overlay-open], a link to /search without
  // JavaScript) or the drawer (event hy:search:overlay-open, sent by
  // assets/hy-header-nav.js) opens it with showModal(). While it is open the
  // panel stays visible without focus (the on-screen keyboard blurs the field).
  #setupOverlay(signal) {
    // Same pattern as the drawer: "open" is static for the no-JS case, and
    // showModal() throws on a dialog that is already open.
    this.#dialog.close();
    this.#desktopQuery = window.matchMedia('(min-width: 48rem)');

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-hy-search-overlay-open]');
      if (!opener || opener.getAttribute('aria-controls') !== this.#dialog.id) return;
      if (this.#desktopQuery.matches) return;
      event.preventDefault();
      this.#openOverlay(opener);
    }, { signal });
    document.addEventListener('hy:search:overlay-open', (event) => {
      this.#openOverlay(event.detail?.opener ?? null);
    }, { signal });
    this.querySelector('[data-hy-search-overlay-close]')?.addEventListener('click', () => {
      this.#dialog.close();
    }, { signal });
    // Escape (cancel) closes natively; every way out ends in 'close'.
    this.#dialog.addEventListener('close', this.#handleOverlayClose, { signal });
    this.#dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && this.#overlayOpen()) this.#trapTab(event);
    }, { signal });
    this.#desktopQuery.addEventListener('change', (event) => {
      if (event.matches && this.#overlayOpen()) this.#dialog.close();
    }, { signal });
  }

  // WebKit lets Tab leave a modal dialog mid-way (the same finding as the
  // drawer, see #trapTab in assets/hy-header-nav.js), so the dialog runs its
  // own Tab order over the visible focusable elements.
  #trapTab(event) {
    const focusable = [
      ...this.#dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    event.preventDefault();
    const current = focusable.indexOf(document.activeElement);
    const step = event.shiftKey ? -1 : 1;
    const next = current === -1
      ? (event.shiftKey ? focusable.length - 1 : 0)
      : (current + step + focusable.length) % focusable.length;
    focusable[next].focus();
  }

  #overlayOpen() {
    return Boolean(this.#dialog?.open && this.#dialog.matches(':modal'));
  }

  #openOverlay(opener) {
    if (!this.#dialog || this.#overlayOpen() || this.#desktopQuery.matches) return;
    this.#opener = opener;
    this.#dialog.removeAttribute('role');
    this.#dialog.showModal();
    this.#overlayActive = true;
    // As the drawer and the cart drawer: showModal() does not lock page
    // scrolling in every browser.
    document.documentElement.style.overflow = 'hidden';
    this.#dismissed = false;
    this.#input.focus();
    this.#setState(this.#restingState());
  }

  #handleOverlayClose = () => {
    // The start-up close() above also fires 'close' (queued): only clean up
    // after a real showModal().
    if (!this.#overlayActive) return;
    this.#overlayActive = false;
    this.#dialog.setAttribute('role', 'none');
    document.documentElement.style.overflow = '';
    this.#cancelPending();
    this.#close();
    const opener = this.#opener;
    this.#opener = null;
    if (opener?.isConnected) opener.focus();
  };

  #setState(state) {
    if (!this.#dropdown) return;
    this.dataset.hySearchState = state;
  }

  #restingState() {
    const typing = this.#input.value.trim().length >= 3;
    const focused = document.activeElement === this.#input || this.#overlayOpen();
    return this.#suggest && focused && !typing && !this.#dismissed ? 'suggest' : 'idle';
  }

  #announce(text) {
    if (this.#status && text) this.#status.textContent = text;
  }

  #handleFocusIn = (event) => {
    if (event.target !== this.#input) return;
    this.#dismissed = false;
    if (this.dataset.hySearchState === 'idle') this.#setState(this.#restingState());
  };

  #syncClear() {
    if (this.#clear) this.#clear.hidden = this.#input.value === '';
  }

  #handleInput = (event) => {
    if (event.target !== this.#input) return;

    this.#syncClear();
    this.#dismissed = false;
    clearTimeout(this.#debounceTimer);
    const term = this.#input.value.trim();

    if (term.length < 3) {
      this.#lastQuery = '';
      this.#requestController?.abort();
      this.#close();
      return;
    }

    // Dedupe: derselbe getrimmte Query löst keine neue Anfrage aus —
    // fängt Pfeiltasten, Shift, IME-Commits ab (M5-Plan §5.3).
    if (term === this.#lastQuery) return;

    // Trailing Debounce 300ms (Dawns Wert, M5-Plan §5.3).
    this.#debounceTimer = setTimeout(() => {
      this.#search(term);
    }, 300);
  };

  #handleKeydown = (event) => {
    if (event.target !== this.#input) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const options = this.#listbox.querySelectorAll('.hy-predictive-search__option');
      if (options.length === 0) return;

      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      // Kein Wrap-Around, der Fokus verlässt nie das Eingabefeld
      // (M5-Plan §5.5).
      const next = this.#activeIndex + delta;
      if (next < 0 || next >= options.length) return;

      this.#setActiveOption(options, next);
      return;
    }

    if (event.key === 'Enter') {
      const activeOption = this.#activeIndex >= 0
        ? this.#listbox.querySelector('.hy-predictive-search__option[aria-selected="true"]')
        : null;
      const link = activeOption?.querySelector('.hy-predictive-search__link');
      if (link) {
        // Eine aktive Option übernimmt den Enter-Druck (WAI-ARIA-APG-
        // Combobox-Muster) — ohne aktive Option KEIN preventDefault,
        // das Formular sendet klassisch an routes.search_url
        // (M5-Plan §5.5, nie geschluckter Volltextpfad).
        event.preventDefault();
        window.location.assign(link.href);
      }
      return;
    }

    if (event.key === 'Escape') {
      // In the mobile search dialog Escape closes the whole dialog (native
      // cancel); the two-stage handling below is for the inline panel.
      if (this.#overlayOpen()) return;
      // Erste Stufe schließt, was offen ist (Liste oder Panel), zweite
      // leert das Feld. preventDefault() in Stufe 1 ist kein Zierrat:
      // <input type="search"> löscht sich in Chromium bei Escape nativ
      // selbst — ohne den Aufruf würde die erste Stufe sofort auch das Feld
      // leeren (live entdeckt, AP-77-Abnahme). #dismissed hält ein per
      // Escape geschlossenes Vorschlagspanel zu, bis erneut getippt oder
      // fokussiert wird.
      const state = this.dataset.hySearchState;
      const open = this.#input.getAttribute('aria-expanded') === 'true'
        || (state !== undefined && state !== 'idle');
      if (open) {
        event.preventDefault();
        this.#dismissed = true;
        this.#cancelPending();
        this.#close();
        return;
      }
      if (this.#input.value !== '') {
        // Also stop a search still waiting in the typing pause: without it the
        // cleared term was searched anyway when no panel was open yet (shop
        // without suggestions; found in the lab, AP-268 stage 4).
        this.#cancelPending();
        this.#input.value = '';
        this.#syncClear();
      }
    }
  };

  #handleFocusOut = (event) => {
    if (this.contains(event.relatedTarget) || this.#overlayOpen()) return;
    this.#cancelPending();
    this.#close();
  };

  // Closing must also stop what is still on its way: without this, a search
  // started by the debounce timer or a late response would reopen the panel
  // after Escape or blur (reviewer finding, AP-268 stage 2).
  #cancelPending() {
    clearTimeout(this.#debounceTimer);
    this.#requestController?.abort();
    this.#lastQuery = '';
  }

  #setActiveOption(options, index) {
    options.forEach((option) => option.removeAttribute('aria-selected'));
    const option = options[index];
    option.setAttribute('aria-selected', 'true');
    this.#activeIndex = index;
    this.#input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  #search(term) {
    if (this.#disabled) return;
    if (Date.now() < this.#blockedUntil) return;

    this.#lastQuery = term;
    this.#requestController?.abort();
    this.#requestController = new AbortController();

    const url = new URL(this.#input.dataset.url, window.location.href);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '5');
    // resources[limit_scope]=each (Default ist all): ohne diesen Parameter
    // teilen sich Produkte und Kollektionen ein gemeinsames Fünferlimit
    // statt je bis zu fünf getrennt zu bekommen (PR-3-Abnahme, reviewer-
    // Fund, gegen shopify.dev/docs/api/ajax/reference/predictive-search).
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.#input.dataset.sectionId || 'hy-predictive-search');

    // Placeholder rows only while nothing is shown yet: typing on with
    // results open keeps the old hits until the new ones arrive (no flicker).
    if (this.dataset.hySearchState !== 'results') this.#setState('loading');
    this.#announce(this.#dropdown?.dataset.loadingLabel);

    fetch(url.toString(), { signal: this.#requestController.signal })
      .then((response) => {
        if (response.status === 429) {
          const retryAfterHeader = response.headers.get('Retry-After');
          const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : 5000;
          this.#blockedUntil = Date.now() + (Number.isFinite(retryAfterMs) ? retryAfterMs : 5000);
          this.#close();
          return null;
        }
        if (response.status === 404 || response.status === 417 || response.status === 422) {
          this.#disabled = true;
          if (response.status !== 417) {
            // eslint-disable-next-line no-console
            console.warn(`hy-predictive-search: status ${response.status}`);
          }
          this.#close();
          return null;
        }
        if (!response.ok) {
          // 5xx/unerwartet: still schließen, kein Warn-Log — kein
          // dauerhafter Theme-Bug, eher ein transientes Server-Problem
          // (M5-Plan §5.4).
          this.#close();
          return null;
        }
        return response.text();
      })
      .then((responseText) => {
        if (!responseText) return;
        this.#render(responseText, term);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Echter Netzwerkfehler (fetch() selbst schlägt fehl, z. B.
        // offline) — still schließen, kein Log (M5-Plan §5.4).
        this.#close();
      });
  }

  #render(responseText, term) {
    // Safety net for a response that slipped past the abort: a panel the
    // visitor closed stays closed.
    const engaged = document.activeElement === this.#input || this.#overlayOpen();
    if (this.#dropdown && (this.#dismissed || !engaged)) return;
    const responseDoc = new DOMParser().parseFromString(responseText, 'text/html');
    // The Section Rendering API wraps the output in a shopify-section div,
    // so the parts are looked up anywhere in the response, not as children.
    const parts = Array.from(responseDoc.querySelectorAll('[data-hy-search-part]'));
    const options = Array.from(responseDoc.querySelectorAll('.hy-predictive-search__option'));

    if (options.length === 0) {
      this.#showEmpty(term);
      return;
    }

    options.forEach((option, index) => {
      option.id = `${this.#listbox.id}-option-${index}`;
    });

    const heading = this.#dropdown?.dataset.collectionsHeading;
    if (heading) {
      responseDoc.querySelectorAll('[data-hy-search-collections-heading]').forEach((el) => {
        el.textContent = heading;
        el.closest('[role="group"]')?.setAttribute('aria-label', heading);
      });
    }

    this.#listbox.replaceChildren(...(parts.length > 0 ? parts : options));
    this.#activeIndex = -1;
    this.#input.removeAttribute('aria-activedescendant');
    this.#input.setAttribute('aria-expanded', 'true');
    this.#setState('results');
    this.#announce(this.#dropdown?.dataset.readyLabel);
  }

  #showEmpty(term) {
    const empty = this.#dropdown?.querySelector('[data-hy-search-empty]');
    if (!empty) {
      this.#close();
      return;
    }
    // textContent, never innerHTML: the term is user input.
    empty.querySelectorAll('[data-hy-search-term]').forEach((el) => {
      el.textContent = term;
    });
    this.#listbox.replaceChildren();
    this.#activeIndex = -1;
    this.#input.removeAttribute('aria-activedescendant');
    this.#input.setAttribute('aria-expanded', 'false');
    this.#setState('empty');
    this.#announce(empty.querySelector('.hy-header__search-empty-title')?.textContent);
  }

  #close() {
    this.#input.setAttribute('aria-expanded', 'false');
    this.#input.removeAttribute('aria-activedescendant');
    this.#activeIndex = -1;
    this.#listbox.replaceChildren();
    if (this.#status) this.#status.textContent = '';
    this.#setState(this.#restingState());
  }
}

customElements.define('hy-predictive-search', HyPredictiveSearch);
